import { useRef, useState, useCallback, useEffect } from "react";
import { motion, animate, useInView } from "framer-motion";
import { ChevronsLeftRight } from "lucide-react";
import { BEFORE_AFTER as PAIRS } from "../lib/constants";

export function Compare({ pair, index = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });
  const [pos, setPos] = useState(50);
  const [dragging, setDragging] = useState(false);
  const demoed = useRef(false);

  useEffect(() => {
    if (inView && !demoed.current) {
      demoed.current = true;
      const controls = animate(50, [50, 64, 50], {
        duration: 1.8,
        delay: 0.5 + index * 0.15,
        ease: "easeInOut",
        onUpdate: (v) => setPos(v),
      });
      return () => controls.stop();
    }
  }, [inView, index]);

  const update = useCallback((clientX) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    setPos(Math.min(97, Math.max(3, ((clientX - r.left) / r.width) * 100)));
  }, []);

  const onPointerDown = (e) => {
    setDragging(true);
    e.currentTarget.setPointerCapture?.(e.pointerId);
    update(e.clientX);
  };

  return (
    <motion.figure
      initial={{ opacity: 0, y: 36 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.8, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className="group"
    >
      <div
        ref={ref}
        data-testid={`before-after-${pair.key}`}
        onPointerDown={onPointerDown}
        onPointerMove={(e) => dragging && update(e.clientX)}
        onPointerUp={() => setDragging(false)}
        onPointerCancel={() => setDragging(false)}
        className="relative w-full aspect-[16/10] overflow-hidden rounded-3xl border border-white/10 select-none touch-none cursor-ew-resize"
        style={{ boxShadow: "0 40px 90px -40px rgba(0,0,0,0.9)" }}
      >
        {/* AFTER (full, underneath) */}
        <img src={pair.after} alt={`${pair.caption} — after`} className="absolute inset-0 w-full h-full object-cover" draggable={false} />

        {/* BEFORE (clipped to the left of the handle) */}
        <img
          src={pair.before}
          alt={`${pair.caption} — before`}
          draggable={false}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
        />

        {/* labels */}
        <span
          className="absolute top-5 left-5 rounded-full bg-midnight/70 backdrop-blur px-4 py-1.5 font-mono text-[10px] tracking-[0.3em] uppercase text-white/90 transition-opacity duration-300"
          style={{ opacity: pos > 15 ? 1 : 0 }}
          data-testid={`label-before-${pair.key}`}
        >
          Before
        </span>
        <span className="absolute top-5 right-5 rounded-full bg-crystal/25 backdrop-blur px-4 py-1.5 font-mono text-[10px] tracking-[0.3em] uppercase text-crystal">
          After
        </span>

        {/* handle */}
        <div className="absolute inset-y-0" style={{ left: `${pos}%` }}>
          <div className="absolute inset-y-0 -left-px w-[3px] bg-white/90" style={{ boxShadow: "0 0 24px rgba(0,229,255,0.7)" }} />
          <div
            className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 rounded-full bg-crystal text-midnight flex items-center justify-center w-12 h-12 transition-transform duration-150 ${
              dragging ? "scale-110" : "group-hover:scale-105"
            }`}
            style={{ boxShadow: "0 0 30px rgba(0,229,255,0.55)" }}
          >
            <ChevronsLeftRight size={20} strokeWidth={2.2} />
          </div>
        </div>

        {/* caption bar */}
        <figcaption className="absolute bottom-5 left-5 right-5 flex items-end justify-between gap-4 pointer-events-none">
          <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-white/85">{pair.caption}</span>
          <span className="hidden sm:block w-10 h-[1px] bg-crystal shrink-0 mb-1" />
        </figcaption>
      </div>
    </motion.figure>
  );
}

export default function BeforeAfter() {
  return (
    <section className="relative py-24 sm:py-32">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7 }}
          className="flex flex-wrap items-end justify-between gap-6 mb-14"
        >
          <div>
            <div className="flex items-center gap-3 mb-5">
              <span className="w-9 h-[1px] bg-crystal" />
              <span className="font-mono text-[11px] tracking-[0.35em] uppercase text-crystal">Before / After</span>
            </div>
            <h2 className="font-display font-medium text-[clamp(2.2rem,5vw,4.2rem)] leading-[1.02] text-white tracking-tight">
              Grab the line.
              <br />
              <em className="italic text-crystal">Drag it through the glass.</em>
            </h2>
          </div>
          <p className="max-w-md text-mist text-sm leading-relaxed">
            Every job ends the same way — glass so clear the window stops
            existing. Slide each one and watch the light come back.
          </p>
        </motion.div>

        <div className="space-y-8 lg:space-y-10">
          {PAIRS.map((p, i) => (
            <Compare key={p.key} pair={p} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
